/**
 * Custom Filters - Handles discount filtering and size facet grouping
 * These are client-side supplementary behaviors that work alongside Shopify's native filters
 */

import { Component } from '@theme/component';
import { FilterAvailabilityDataSource } from '@theme/filter-availability';
import {
  buildSizeAvailabilityCountsForNativeColorResults,
  collectMutationCandidateNodes,
  countMatchingProductsForNativeColorResults,
  formatSeeResultsLabel,
  formatVisibleCountLabel,
} from '@theme/custom-filters-logic';

const HIDDEN_BY_DISCOUNT_FILTER = 'data-hidden-by-discount-filter';
const HIDDEN_BY_VARIANT_AVAILABILITY = 'data-hidden-by-variant-availability';

function getProductCardVisibilityTarget(card) {
  return card.closest('.product-grid__item') || card;
}

function syncProductCardVisibility(card) {
  const target = getProductCardVisibilityTarget(card);
  const shouldHide =
    card.hasAttribute(HIDDEN_BY_DISCOUNT_FILTER) || card.hasAttribute(HIDDEN_BY_VARIANT_AVAILABILITY);

  target.style.display = shouldHide ? 'none' : '';
}

function setProductCardFilterVisibility(card, attributeName, shouldShow) {
  if (!(card instanceof Element)) return;

  if (shouldShow) {
    card.removeAttribute(attributeName);
  } else {
    card.setAttribute(attributeName, 'true');
  }

  syncProductCardVisibility(card);
}

function getVisibleProductCardCount(productCards) {
  return Array.from(productCards || []).filter(
    (card) =>
      !card.hasAttribute(HIDDEN_BY_DISCOUNT_FILTER) && !card.hasAttribute(HIDDEN_BY_VARIANT_AVAILABILITY)
  ).length;
}

/**
 * Discount Status Component - Updates the status display for discount filter
 */
class DiscountStatusComponent extends Component {
  requiredRefs = ['discountStatus'];

  updateStatus(selectedRanges) {
    if (!this.refs.discountStatus) return;

    if (selectedRanges.length === 0) {
      this.refs.discountStatus.textContent = '';
      return;
    }

    if (selectedRanges.length === 1) {
      const rangeLabels = {
        'full-price': 'Full Price',
        '0-20': 'Up to 20%',
        '20-40': '20-40%',
        '40-60': '40-60%',
        '60-100': '60%+'
      };
      this.refs.discountStatus.textContent = rangeLabels[selectedRanges[0]] || '';
    } else {
      this.refs.discountStatus.textContent = selectedRanges.length.toString();
    }
  }
}

if (!customElements.get('discount-status-component')) {
  customElements.define('discount-status-component', DiscountStatusComponent);
}

/**
 * Discount Facet Component - Handles discount percentage filtering
 */
class DiscountFacetComponent extends Component {
  requiredRefs = ['discountInputs'];

  connectedCallback() {
    super.connectedCallback();
    this.attachEventListeners();
    // Hide empty filter options after a short delay to let product cards render
    requestAnimationFrame(() => {
      setTimeout(() => this.hideEmptyOptions(), 500);
    });
  }

  /**
   * Hide discount filter options that have no matching products
   */
  hideEmptyOptions() {
    const productCards = document.querySelectorAll('product-card[data-price]');
    const rangeCounts = { 'full-price': 0, '0-20': 0, '20-40': 0, '40-60': 0, '60-100': 0 };

    productCards.forEach(card => {
      const price = parseFloat(card.getAttribute('data-price'));
      const compareAtPrice = parseFloat(card.getAttribute('data-compare-at-price'));
      const discount = this.calculateDiscount(price, compareAtPrice);

      if (discount === 0) {
        rangeCounts['full-price']++;
      }
      if (discount > 0 && discount <= 20) rangeCounts['0-20']++;
      if (discount > 20 && discount <= 40) rangeCounts['20-40']++;
      if (discount > 40 && discount <= 60) rangeCounts['40-60']++;
      if (discount > 60) rangeCounts['60-100']++;
    });

    Object.entries(rangeCounts).forEach(([range, count]) => {
      const listItem = this.querySelector(`[data-discount-range="${range}"]`);
      if (listItem) {
        listItem.style.display = count > 0 ? '' : 'none';
      }
    });
  }

  attachEventListeners() {
    if (!this.refs.discountInputs) return;

    this.refs.discountInputs.forEach(input => {
      input.addEventListener('change', () => this.applyDiscountFilter());
    });
  }

  /**
   * Calculate discount percentage for a product
   */
  calculateDiscount(price, compareAtPrice) {
    if (!compareAtPrice || compareAtPrice <= price) return 0;
    return ((compareAtPrice - price) / compareAtPrice) * 100;
  }

  /**
   * Check if discount falls within a range
   */
  isInRange(discount, range) {
    if (range === 'full-price') return discount === 0;
    const [min, max] = range.split('-').map(Number);
    return discount > min && discount <= max;
  }

  /**
   * Apply discount visibility to a card.
   * @param {Element} card
   * @param {boolean} shouldShowByDiscount
   */
  setCardVisibility(card, shouldShowByDiscount) {
    setProductCardFilterVisibility(card, HIDDEN_BY_DISCOUNT_FILTER, shouldShowByDiscount);
  }

  /**
   * Apply discount filter to product cards
   */
  applyDiscountFilter() {
    const selectedRanges = Array.from(this.refs.discountInputs)
      .filter(input => input.checked)
      .map(input => input.value);

    // Update status display
    const statusComponent = this.closest('.discount-filter')?.querySelector('discount-status-component');
    if (statusComponent) {
      statusComponent.updateStatus(selectedRanges);
    }

    // Show/hide discount clear pill in the facets-remove bar
    this.updateDiscountClearPills(selectedRanges);

    // Get all product cards
    const productCards = document.querySelectorAll('product-card[data-price]');

    if (selectedRanges.length === 0) {
      // No filters selected - show all cards
      productCards.forEach(card => {
        this.setCardVisibility(card, true);
      });
      return;
    }

    // Apply filter
    productCards.forEach(card => {
      const price = parseFloat(card.getAttribute('data-price'));
      const compareAtPrice = parseFloat(card.getAttribute('data-compare-at-price'));

      if (!price) {
        this.setCardVisibility(card, true);
        return;
      }

      const discount = this.calculateDiscount(price, compareAtPrice);

      // Check if product matches any selected range
      const matchesFilter = selectedRanges.some(range => this.isInRange(discount, range));
      this.setCardVisibility(card, matchesFilter);
    });

    // Update product count
    this.updateProductCount(productCards);
  }

  /**
   * Update discount clear pills in the facets-remove bar
   */
  updateDiscountClearPills(selectedRanges) {
    // Remove old discount pills
    document.querySelectorAll('.facets-remove .discount-clear-pill').forEach(el => el.remove());

    if (selectedRanges.length === 0) {
      // Also hide discount clear-all if no other active filters
      document.querySelectorAll('.facets-remove .discount-clear-all-pill').forEach(el => el.remove());
      return;
    }

    const rangeLabels = {
      'full-price': 'Full Price',
      '0-20': 'Up to 20% off',
      '20-40': '20-40% off',
      '40-60': '40-60% off',
      '60-100': '60%+ off'
    };

    // Find the facets-remove containers and add discount pills
    document.querySelectorAll('.facets-remove').forEach(container => {
      selectedRanges.forEach(range => {
        const pill = document.createElement('span');
        pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill discount-clear-pill';
        pill.setAttribute('tabindex', '0');
        pill.setAttribute('role', 'button');
        pill.innerHTML = `${rangeLabels[range] || range} <span class="svg-wrapper svg-wrapper--smaller"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="12" height="12"><line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" stroke-width="1.5"/><line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" stroke-width="1.5"/></svg></span>`;
        pill.addEventListener('click', () => {
          // Uncheck this specific discount range
          if (this.refs.discountInputs) {
            this.refs.discountInputs.forEach(input => {
              if (input.value === range) input.checked = false;
            });
            this.applyDiscountFilter();
          }
        });
        container.appendChild(pill);
      });

      // Add a "Clear all" link if there isn't one already visible and we have discount filters active
      const existingClearAll = container.querySelector('.facets__clear-all-link, .discount-clear-all-pill');
      if (!existingClearAll) {
        const clearAll = document.createElement('span');
        clearAll.className = 'discount-clear-all-pill';
        clearAll.innerHTML = `<button type="button" class="button-unstyled facets__clear-all-link">Clear all</button>`;
        clearAll.querySelector('button').addEventListener('click', () => {
          // Clear discount filters
          if (this.refs.discountInputs) {
            this.refs.discountInputs.forEach(input => { input.checked = false; });
            this.applyDiscountFilter();
          }
          // Also trigger native clear if there's a form
          const form = container.closest('form') || document.querySelector('.facets__form');
          if (form) {
            const clearLink = form.querySelector('a[href*="collections"]');
            if (clearLink) clearLink.click();
          }
        });
        container.appendChild(clearAll);
      }
    });
  }

  /**
   * Update the product count display
   */
  updateProductCount(productCards) {
    const visibleCount = getVisibleProductCardCount(productCards);

    const countWrappers = document.querySelectorAll('.products-count-wrapper span');
    countWrappers.forEach(wrapper => {
      if (visibleCount > 25000) {
        wrapper.textContent = `25,000+ items`;
      } else {
        const pluralized = visibleCount === 1 ? 'item' : 'items';
        wrapper.textContent = `${visibleCount.toLocaleString()} ${pluralized}`;
      }
    });
  }
}

if (!customElements.get('discount-facet-component')) {
  customElements.define('discount-facet-component', DiscountFacetComponent);
}

/**
 * Discount Clear Component - Handles clearing discount filter
 */
class DiscountClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.attachEventListeners();
  }

  attachEventListeners() {
    const clearButton = this.refs.clearButton?.querySelector('button');
    if (!clearButton) return;

    clearButton.addEventListener('click', () => {
      const facetComponent = this.closest('discount-facet-component');
      if (facetComponent && facetComponent.refs.discountInputs) {
        facetComponent.refs.discountInputs.forEach(input => {
          input.checked = false;
        });
        facetComponent.applyDiscountFilter();
      }
    });
  }
}

if (!customElements.get('discount-clear-component')) {
  customElements.define('discount-clear-component', DiscountClearComponent);
}

const DEFAULT_SIZE_ALIAS_MAPPING = `SMALL = S
MEDIUM = M
LARGE = L
EXTRA LARGE = XL
X LARGE = XL
X-LARGE = XL
XXL = 2XL
XX LARGE = 2XL
XX-LARGE = 2XL
XXXL = 3XL
XXXXL = 4XL
ONE SIZE = OS
ONE SIZE FITS ALL = OS`;

const DEFAULT_SIZE_ORDER = `S
M
L
XL
2XL
3XL
4XL
28
29
30
31
32
33
34
36
38
40
42
44
S/M
L/XL
OS
12-PACK
25-PACK`;

const DEFAULT_SIZE_GROUPS = `TOPS = S, M, L, XL, 2XL, 3XL, 4XL
BOTTOMS = 28, 29, 30, 31, 32, 33, 34, 36, 38, 40, 42, 44
HATS & AXS = S/M, L/XL, OS, 12-PACK, 25-PACK`;

class SizeFacetGroupingManager {
  constructor({ sizeGroupsConfig = '', sizeAliasConfig = '', sizeOrderConfig = '' } = {}) {
    this.sizeAliasLookup = this.parseSizeAliasMapping(sizeAliasConfig || DEFAULT_SIZE_ALIAS_MAPPING);
    this.sizeOrderLookup = this.parseSizeOrder(sizeOrderConfig || DEFAULT_SIZE_ORDER);
    this.sizeGroups = this.parseSizeGroups(sizeGroupsConfig || DEFAULT_SIZE_GROUPS);
    this.sizeGroupLookup = this.buildSizeGroupLookup(this.sizeGroups);
    this.pendingApply = null;
    this.observer = null;
    this.isApplying = false;

    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onFilterUpdate = this.onFilterUpdate.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onDocumentMutation = this.onDocumentMutation.bind(this);
  }

  init() {
    this.apply();
    this.observeFacetMutations();
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('filter:update', this.onFilterUpdate);
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('pageshow', this.onPageShow);
  }

  destroy() {
    if (this.pendingApply) {
      clearTimeout(this.pendingApply);
      this.pendingApply = null;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('filter:update', this.onFilterUpdate);
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('pageshow', this.onPageShow);

    document
      .querySelectorAll('accordion-custom[data-filter-param-name*="size" i] ul.facets__inputs-list')
      .forEach((list) => this.clearManagedNodes(list));
  }

  observeFacetMutations() {
    if (!document.body || typeof MutationObserver === 'undefined') return;

    this.observer = new MutationObserver(this.onDocumentMutation);
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  nodeIsManagedSizeNode(node) {
    if (!(node instanceof Element)) return false;
    return node.matches('[data-size-group-managed="true"]') || !!node.closest('[data-size-group-managed="true"]');
  }

  nodeTouchesSizeFacet(node) {
    if (!(node instanceof Element)) return false;

    return (
      node.matches(
        'facet-filters-form, facet-filters-form *, accordion-custom[data-filter-param-name*="size" i], accordion-custom[data-filter-param-name*="size" i] *'
      ) || !!node.querySelector('accordion-custom[data-filter-param-name*="size" i]')
    );
  }

  onDocumentMutation(mutations) {
    if (this.isApplying) return;

    const shouldApply = mutations.some((mutation) => {
      const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes].filter(
        (node) => node.nodeType === Node.ELEMENT_NODE
      );

      if (changedNodes.length > 0 && changedNodes.every((node) => this.nodeIsManagedSizeNode(node))) {
        return false;
      }

      const target = mutation.target instanceof Element ? mutation.target : mutation.target?.parentElement;
      return this.nodeTouchesSizeFacet(target) || changedNodes.some((node) => this.nodeTouchesSizeFacet(node));
    });

    if (shouldApply) this.scheduleApply(60);
  }

  onSectionLoad() {
    this.scheduleApply(20);
  }

  onFilterUpdate() {
    this.scheduleApply(120);
  }

  onPopState() {
    this.scheduleApply(80);
  }

  onPageShow() {
    this.scheduleApply(0);
  }

  scheduleApply(delay = 0) {
    if (this.pendingApply) clearTimeout(this.pendingApply);
    this.pendingApply = setTimeout(() => {
      this.pendingApply = null;
      this.apply();
    }, delay);
  }

  normalizeToken(value) {
    return (value || '')
      .toString()
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '');
  }

  normalizeSizeValue(value) {
    const raw = (value || '').toString().trim();
    if (!raw) return '';

    const token = this.normalizeToken(raw);
    if (!token) return '';

    const builtInAliases = {
      SMALL: 'S',
      MEDIUM: 'M',
      LARGE: 'L',
      EXTRALARGE: 'XL',
      XLARGE: 'XL',
      XXLARGE: '2XL',
      XXXLARGE: '3XL',
      XXXXLARGE: '4XL',
      ONESIZE: 'OS',
      ONESIZEFITSALL: 'OS',
    };

    const mapped = this.sizeAliasLookup instanceof Map ? this.sizeAliasLookup.get(token) : null;
    return mapped || builtInAliases[token] || raw.toUpperCase();
  }

  normalizeGroupName(value) {
    const groupName = (value || '').toString().trim().toUpperCase();
    if (groupName.includes('ACCESSORIES') || groupName.includes('AXS')) return 'HATS & AXS';
    return groupName;
  }

  parseSizeAliasMapping(config) {
    const lookup = new Map();

    (config || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const separatorIndex = line.indexOf('=');
        if (separatorIndex === -1) return;

        const source = this.normalizeToken(line.slice(0, separatorIndex));
        const canonical = this.normalizeSizeValue(line.slice(separatorIndex + 1));
        if (!source || !canonical) return;
        lookup.set(source, canonical);
      });

    return lookup;
  }

  parseSizeOrder(config) {
    const lookup = new Map();

    (config || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line, index) => {
        const normalized = this.normalizeSizeValue(line);
        if (!normalized) return;
        if (!lookup.has(normalized)) lookup.set(normalized, index);
      });

    return lookup;
  }

  parseSizeGroups(config) {
    return (config || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const separatorIndex = line.indexOf('=');
        if (separatorIndex === -1) return null;

        const name = this.normalizeGroupName(line.slice(0, separatorIndex));
        const values = line
          .slice(separatorIndex + 1)
          .split(',')
          .map((value) => this.normalizeSizeValue(value))
          .filter(Boolean);

        if (!name || values.length === 0) return null;
        return { name, values };
      })
      .filter(Boolean);
  }

  buildSizeGroupLookup(groups) {
    const lookup = new Map();

    (groups || []).forEach((group, groupIndex) => {
      group.values.forEach((value, valueIndex) => {
        if (!lookup.has(value)) {
          lookup.set(value, { groupIndex, valueIndex });
        }
      });
    });

    return lookup;
  }

  clearManagedNodes(list) {
    list.querySelectorAll(':scope > li[data-size-group-managed="true"]').forEach((node) => {
      node.remove();
    });
  }

  getLabelNode(listItem) {
    return (
      listItem.querySelector('.checkbox__label-text') ||
      listItem.querySelector('.facets__swatch-label') ||
      listItem.querySelector('.facets__pill-label') ||
      listItem.querySelector('.facets__label') ||
      listItem.querySelector('label')
    );
  }

  getFacetLabel(listItem, input) {
    const dataLabel = (input.dataset.label || '').toString().trim();
    if (dataLabel) return dataLabel;

    const ariaLabel = (input.getAttribute('aria-label') || '').toString().trim();
    if (ariaLabel) return ariaLabel;

    const labelNode = this.getLabelNode(listItem);
    if (!labelNode) return '';

    const countNode = labelNode.querySelector('.facets__count');
    const text = Array.from(labelNode.childNodes)
      .filter((node) => node !== countNode)
      .map((node) => node.textContent || '')
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return text;
  }

  getOriginalIndex(item, index) {
    const cached = Number.parseInt(item.dataset.sizeOriginalIndex || '', 10);
    if (!Number.isNaN(cached)) return cached;

    item.dataset.sizeOriginalIndex = String(index);
    return index;
  }

  compareEntries(left, right) {
    if (left.groupSortIndex !== right.groupSortIndex) return left.groupSortIndex - right.groupSortIndex;
    if (left.groupValueIndex !== right.groupValueIndex) return left.groupValueIndex - right.groupValueIndex;
    if (left.sizeOrderIndex !== right.sizeOrderIndex) return left.sizeOrderIndex - right.sizeOrderIndex;
    return left.originalIndex - right.originalIndex;
  }

  getGroupPriority(groupName, fallbackIndex) {
    const normalized = this.normalizeGroupName(groupName);
    if (normalized === 'TOPS') return 0;
    if (normalized === 'BOTTOMS') return 1;
    if (normalized === 'HATS & AXS') return 2;
    return 10 + (Number.isFinite(fallbackIndex) ? fallbackIndex : 0);
  }

  buildHeader(groupName, isFirst) {
    const header = document.createElement('li');
    header.className = 'facets__size-group-header';
    if (isFirst) {
      header.classList.add('facets__size-group-header--first');
    }
    header.setAttribute('aria-hidden', 'true');
    header.setAttribute('data-size-group-managed', 'true');
    header.setAttribute('data-skip-node-update', 'true');

    const textNode = document.createElement('span');
    textNode.className = 'facets__size-group-header-text';
    textNode.textContent = groupName;
    header.appendChild(textNode);

    return header;
  }

  buildColumnBreak() {
    const columnBreak = document.createElement('li');
    columnBreak.className = 'facets__size-column-break';
    columnBreak.setAttribute('aria-hidden', 'true');
    columnBreak.setAttribute('data-size-group-managed', 'true');
    columnBreak.setAttribute('data-skip-node-update', 'true');
    return columnBreak;
  }

  applyToAccordion(accordion) {
    const list = accordion.querySelector('ul.facets__inputs-list');
    if (!list) return;

    this.clearManagedNodes(list);

    const entries = [];
    list.querySelectorAll(':scope > li.facets__inputs-list-item').forEach((item, index) => {
      const input = item.querySelector('input[type="checkbox"]');
      if (!(input instanceof HTMLInputElement)) return;

      const label = this.getFacetLabel(item, input);
      const normalizedSize = this.normalizeSizeValue(label);
      if (!normalizedSize) return;

      const originalIndex = this.getOriginalIndex(item, index);
      const groupMeta = this.sizeGroupLookup.get(normalizedSize);
      const groupName = groupMeta ? this.sizeGroups[groupMeta.groupIndex]?.name : '';

      entries.push({
        item,
        normalizedSize,
        originalIndex,
        groupIndex: groupMeta ? groupMeta.groupIndex : Number.MAX_SAFE_INTEGER,
        groupSortIndex: groupMeta
          ? this.getGroupPriority(groupName, groupMeta.groupIndex)
          : Number.MAX_SAFE_INTEGER,
        groupValueIndex: groupMeta ? groupMeta.valueIndex : Number.MAX_SAFE_INTEGER,
        sizeOrderIndex: this.sizeOrderLookup.get(normalizedSize) ?? Number.MAX_SAFE_INTEGER,
      });
    });

    if (!entries.length) return;

    entries.sort((left, right) => this.compareEntries(left, right));

    const groupedEntries = [];
    entries.forEach((entry) => {
      const groupKey = entry.groupIndex === Number.MAX_SAFE_INTEGER ? 'OTHER' : String(entry.groupIndex);
      let group = groupedEntries[groupedEntries.length - 1];
      if (!group || group.key !== groupKey) {
        group = {
          key: groupKey,
          name:
            entry.groupIndex === Number.MAX_SAFE_INTEGER
              ? 'OTHER'
              : this.sizeGroups[entry.groupIndex]?.name || 'OTHER',
          entries: [],
        };
        groupedEntries.push(group);
      }
      group.entries.push(entry);
    });

    const fragment = document.createDocumentFragment();
    groupedEntries.forEach((group, groupIndex) => {
      fragment.appendChild(this.buildHeader(group.name, groupIndex === 0));

      const splitIndex = Math.ceil(group.entries.length / 2);
      group.entries.forEach((entry, entryIndex) => {
        if (entryIndex === splitIndex && splitIndex < group.entries.length) {
          fragment.appendChild(this.buildColumnBreak());
        }
        fragment.appendChild(entry.item);
      });
    });

    list.appendChild(fragment);
  }

  apply() {
    this.isApplying = true;

    try {
      document
        .querySelectorAll('accordion-custom[data-filter-param-name*="size" i]')
        .forEach((accordion) => this.applyToAccordion(accordion));
    } finally {
      setTimeout(() => {
        this.isApplying = false;
      }, 0);
    }
  }
}

class VariantAvailabilityDisplayManager {
  constructor() {
    this.pendingApply = null;
    this.observer = null;
    this.isApplying = false;
    this.availabilityDataSource = new FilterAvailabilityDataSource();
    this.availabilityDataset = null;
    this.requestedCacheKey = '';

    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onFilterUpdate = this.onFilterUpdate.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onDocumentMutation = this.onDocumentMutation.bind(this);
  }

  init() {
    this.observeMutations();
    this.refreshAvailabilityDataset();
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('filter:update', this.onFilterUpdate);
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('pageshow', this.onPageShow);
  }

  destroy() {
    if (this.pendingApply) {
      clearTimeout(this.pendingApply);
      this.pendingApply = null;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('filter:update', this.onFilterUpdate);
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('pageshow', this.onPageShow);
    this.requestedCacheKey = '';
    this.availabilityDataset = null;

    document.querySelectorAll(`product-card[${HIDDEN_BY_VARIANT_AVAILABILITY}]`).forEach((card) => {
      setProductCardFilterVisibility(card, HIDDEN_BY_VARIANT_AVAILABILITY, true);
    });
  }

  getObservationRoot() {
    return document.querySelector('results-list')?.closest('.shopify-section') || document.querySelector('main');
  }

  observeMutations() {
    if (typeof MutationObserver === 'undefined') return;

    const root = this.getObservationRoot();
    if (!root) return;

    if (this.observer) this.observer.disconnect();

    this.observer = new MutationObserver(this.onDocumentMutation);
    this.observer.observe(root, { childList: true, subtree: true });
  }

  nodeTouchesRelevantContent(node) {
    if (!(node instanceof Element)) return false;

    return (
      node.matches(
        '.product-grid, .product-grid__item, product-card[data-variants-stock], facet-filters-form, accordion-custom[data-filter-param-name*="size" i]'
      ) ||
      !!node.querySelector(
        '.product-grid, .product-grid__item, product-card[data-variants-stock], facet-filters-form, accordion-custom[data-filter-param-name*="size" i]'
      )
    );
  }

  onDocumentMutation(mutations) {
    if (this.isApplying) return;

    const shouldApply = collectMutationCandidateNodes(mutations).some((node) =>
      this.nodeTouchesRelevantContent(node)
    );

    if (shouldApply) this.scheduleApply(80);
  }

  onSectionLoad() {
    this.observeMutations();
    this.refreshAvailabilityDataset();
  }

  onFilterUpdate() {
    this.refreshAvailabilityDataset();
  }

  onPopState() {
    setTimeout(() => this.refreshAvailabilityDataset(), 0);
  }

  onPageShow() {
    this.refreshAvailabilityDataset();
  }

  scheduleApply(delay = 0) {
    if (this.pendingApply) clearTimeout(this.pendingApply);
    this.pendingApply = setTimeout(() => {
      this.pendingApply = null;
      this.apply();
    }, delay);
  }

  normalizeSizeValue(value) {
    if (sizeFacetGroupingInstance) {
      return sizeFacetGroupingInstance.normalizeSizeValue(value);
    }

    return (value || '').toString().trim().toUpperCase();
  }

  splitParamValue(value) {
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  getSelectedFacetValues(paramName, normalizeValue) {
    const params = new URLSearchParams(window.location.search);
    const selectedRawValues = new Set();

    params.getAll(paramName).forEach((rawValue) => {
      this.splitParamValue(rawValue).forEach((value) => selectedRawValues.add(value));
    });

    const values = new Set();
    if (!selectedRawValues.size) return values;

    const inputs = Array.from(document.querySelectorAll(`input[name="${paramName}"]`)).filter(
      (input) => input instanceof HTMLInputElement
    );

    selectedRawValues.forEach((rawValue) => {
      const matchingInput = inputs.find((input) => input.value === rawValue);
      const label = matchingInput?.dataset.label || (rawValue.startsWith('gid://') ? '' : rawValue);
      const normalized = normalizeValue.call(this, label);

      if (normalized) values.add(normalized);
    });

    return values;
  }

  updateVisibleCountTargets(count) {
    const normalizedCount = Number.isFinite(count) ? Math.max(0, count) : 0;
    const countLabel = formatVisibleCountLabel(normalizedCount);
    const ctaLabel = formatSeeResultsLabel(normalizedCount);

    document.querySelectorAll('[data-visible-count-target]').forEach((target) => {
      target.textContent = target.dataset.visibleCountTarget === 'cta' ? ctaLabel : countLabel;
    });
  }

  getSizeFacetCountNode(input) {
    const listItem = input.closest('.facets__inputs-list-item');
    if (listItem) return listItem.querySelector('.facets__count');

    const wrapper = input.closest('.checkbox, .facets__pill-wrapper, fieldset');
    return wrapper?.querySelector('.facets__count') || null;
  }

  getSizeFacetEntries() {
    return Array.from(document.querySelectorAll('input[name="filter.v.option.size"]'))
      .filter((input) => input instanceof HTMLInputElement)
      .map((input) => {
        const normalizedSize = this.normalizeSizeValue(input.dataset.label || input.value);
        if (!normalizedSize) return null;

        return {
          input,
          normalizedSize,
          countNode: this.getSizeFacetCountNode(input),
        };
      })
      .filter(Boolean);
  }

  restoreNativeSizeFacetCounts() {
    this.getSizeFacetEntries().forEach((entry) => {
      if (entry.countNode) {
        const nativeCountText = entry.countNode.getAttribute('data-native-size-count-text');
        if (nativeCountText) entry.countNode.textContent = nativeCountText;
        entry.countNode.removeAttribute('data-variant-availability-count');
      }

      const nativeDisabled = entry.input.getAttribute('data-native-size-disabled');
      if (nativeDisabled != null) {
        entry.input.disabled = nativeDisabled === 'true';
        entry.input.removeAttribute('data-native-size-disabled');
      }
    });
  }

  updateSizeFacetAvailabilityCounts(products) {
    const entries = this.getSizeFacetEntries();
    if (!entries.length) return;

    const candidateSizes = Array.from(new Set(entries.map((entry) => entry.normalizedSize)));
    const sizeCounts = buildSizeAvailabilityCountsForNativeColorResults(products, candidateSizes);

    entries.forEach((entry) => {
      if (!entry.input.hasAttribute('data-native-size-disabled')) {
        entry.input.setAttribute('data-native-size-disabled', String(entry.input.disabled));
      }

      if (entry.countNode) {
        if (!entry.countNode.hasAttribute('data-native-size-count-text')) {
          entry.countNode.setAttribute('data-native-size-count-text', entry.countNode.textContent.trim());
        }

        const count = Number.isFinite(sizeCounts[entry.normalizedSize]) ? sizeCounts[entry.normalizedSize] : 0;
        entry.countNode.textContent = `(${count})`;
        entry.countNode.setAttribute('data-variant-availability-count', 'true');
      }

      const count = Number.isFinite(sizeCounts[entry.normalizedSize]) ? sizeCounts[entry.normalizedSize] : 0;
      const shouldDisable = count <= 0 && !entry.input.checked;
      const listItem = entry.input.closest('.facets__inputs-list-item');
      entry.input.disabled = shouldDisable;
      listItem?.classList.toggle('facets__inputs-list-item--disabled', shouldDisable);
      listItem?.querySelector('.checkbox')?.classList.toggle('checkbox--disabled', shouldDisable);
    });
  }

  normalizeAvailabilityProducts(products) {
    return (Array.isArray(products) ? products : []).map((product) => ({
      productId: String(product.productId || ''),
      variants: (Array.isArray(product.variants) ? product.variants : [])
        .map((variant) => ({
          available: variant?.available === true || variant?.a === true,
          size: this.normalizeSizeValue(variant?.size || variant?.s || ''),
        }))
        .filter((variant) => variant.available && variant.size),
    }));
  }

  refreshAvailabilityDataset(sourceUrl = new URL(window.location.href)) {
    const cacheKey = this.availabilityDataSource.getCacheKey(sourceUrl);
    this.requestedCacheKey = cacheKey;

    this.availabilityDataSource
      .load(sourceUrl)
      .then((dataset) => {
        if (this.requestedCacheKey !== cacheKey) return;
        if (this.availabilityDataSource.getCacheKey(new URL(window.location.href)) !== cacheKey) return;

        this.availabilityDataset = {
          ...dataset,
          products: this.normalizeAvailabilityProducts(dataset.products),
        };
        this.scheduleApply(0);
      })
      .catch((error) => {
        if (this.requestedCacheKey !== cacheKey) return;
        this.availabilityDataset = null;
        this.restoreNativeSizeFacetCounts();
        console.warn('Unable to load stock-aware filter availability', error);
      });
  }

  getCurrentAvailabilityProducts() {
    const currentCacheKey = this.availabilityDataSource.getCacheKey(new URL(window.location.href));
    if (!this.availabilityDataset || this.availabilityDataset.cacheKey !== currentCacheKey) return null;
    return this.availabilityDataset.products;
  }

  apply() {
    const products = this.getCurrentAvailabilityProducts();
    if (!products) {
      this.refreshAvailabilityDataset();
      return;
    }

    const activeSizes = this.getSelectedFacetValues('filter.v.option.size', this.normalizeSizeValue);
    const cards = Array.from(document.querySelectorAll('.product-grid product-card[data-variants-stock]'));
    const productsById = new Map(products.map((product) => [product.productId, product]));

    this.isApplying = true;

    try {
      this.updateSizeFacetAvailabilityCounts(products);

      cards.forEach((card) => {
        const productId = String(card.getAttribute('data-product-id') || '');
        const product = productsById.get(productId);
        const shouldShow =
          Boolean(product) &&
          (activeSizes.size === 0 || countMatchingProductsForNativeColorResults([product], activeSizes) > 0);

        setProductCardFilterVisibility(card, HIDDEN_BY_VARIANT_AVAILABILITY, shouldShow);
      });

      const visibleCount =
        activeSizes.size > 0
          ? countMatchingProductsForNativeColorResults(products, activeSizes)
          : products.length;
      this.updateVisibleCountTargets(visibleCount);
    } finally {
      setTimeout(() => {
        this.isApplying = false;
      }, 0);
    }
  }
}

let sizeFacetGroupingInstance;
let variantAvailabilityDisplayInstance;

function initSizeFacetGrouping() {
  const sizeGroupsConfig = document.querySelector('[data-size-groups-config]')?.getAttribute('data-size-groups') || '';
  const sizeAliasConfig = document.querySelector('[data-size-alias-mapping]')?.getAttribute('data-size-alias-mapping') || '';
  const sizeOrderConfig = document.querySelector('[data-size-order]')?.getAttribute('data-size-order') || '';

  if (sizeFacetGroupingInstance) {
    sizeFacetGroupingInstance.destroy();
  }

  sizeFacetGroupingInstance = new SizeFacetGroupingManager({
    sizeGroupsConfig,
    sizeAliasConfig,
    sizeOrderConfig,
  });
  sizeFacetGroupingInstance.init();
}

function initVariantAvailabilityDisplay() {
  if (variantAvailabilityDisplayInstance) {
    variantAvailabilityDisplayInstance.destroy();
  }

  variantAvailabilityDisplayInstance = new VariantAvailabilityDisplayManager();
  variantAvailabilityDisplayInstance.init();
}

function initCollectionFilterCutover() {
  initSizeFacetGrouping();
  initVariantAvailabilityDisplay();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionFilterCutover, { once: true });
} else {
  initCollectionFilterCutover();
}

export {
  DiscountFacetComponent,
  DiscountStatusComponent,
  DiscountClearComponent,
  SizeFacetGroupingManager,
};
